import React, { useState, useEffect } from 'react';
import { 
  Target, 
  Plus, 
  Check, 
  X, 
  Calendar, 
  Clock, 
  TrendingUp,
  Award,
  Filter,
  Search,
  Edit,
  Trash2,
  Star,
  AlertCircle,
  CheckCircle2,
  BarChart3
} from 'lucide-react';

interface Goal {
  id: string;
  title: string;
  description: string;
  targetSessions: number;
  completedSessions: number;
  targetMinutes: number;
  completedMinutes: number;
  technique: string;
  deadline: string;
  priority: 'low' | 'medium' | 'high';
  completed: boolean;
  createdAt: string;
  category: string;
  subject?: string;
}

interface NewGoal {
  title?: string;
  description?: string;
  targetSessions?: number;
  targetMinutes?: number;
  technique?: string;
  deadline?: string;
  priority?: 'low' | 'medium' | 'high';
  category?: string;
  subject?: string;
}

const TIME_TECHNIQUES = [
  { id: 'pomodoro', name: 'Pomodoro Tekniği' },
  { id: 'flowtime', name: 'Flowtime Tekniği' },
  { id: 'timeboxing', name: 'Timeboxing' },
  { id: 'ultradian', name: 'Ultradian Ritim' },
  { id: 'sprint', name: 'Sprint Tekniği' },
  { id: 'microlearning', name: 'Micro-Learning' }
];

const GOAL_CATEGORIES = [
  { id: 'study', name: 'Çalışma', icon: '📚', color: 'bg-blue-500' },
  { id: 'exam', name: 'Sınav', icon: '📝', color: 'bg-red-500' },
  { id: 'skill', name: 'Beceri', icon: '🎯', color: 'bg-green-500' },
  { id: 'habit', name: 'Alışkanlık', icon: '⚡', color: 'bg-purple-500' },
  { id: 'project', name: 'Proje', icon: '🚀', color: 'bg-orange-500' }
];

const SUBJECTS = [
  'Matematik', 'Türkçe', 'Fen Bilimleri', 'Sosyal Bilgiler', 'İngilizce', 'Din Kültürü'
];

const Goals: React.FC = () => {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [showAddGoal, setShowAddGoal] = useState(false);
  const [showEditGoal, setShowEditGoal] = useState<string | null>(null);
  const [newGoal, setNewGoal] = useState<NewGoal>({
    title: '',
    description: '',
    targetSessions: 1,
    targetMinutes: 60,
    technique: 'pomodoro',
    deadline: '',
    priority: 'medium',
    category: 'study',
    subject: ''
  });
  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Demo veriler
  useEffect(() => {
    const demoGoals: Goal[] = [
      {
        id: '1',
        title: 'Matematik Sınavına Hazırlanmak',
        description: 'LGS matematik konularını tamamlamak ve deneme sınavlarında 18+ net yapmak',
        targetSessions: 30,
        completedSessions: 18,
        targetMinutes: 900,
        completedMinutes: 540,
        technique: 'pomodoro',
        deadline: '2024-03-15',
        priority: 'high',
        completed: false,
        createdAt: '2024-01-15',
        category: 'exam',
        subject: 'Matematik'
      },
      {
        id: '2',
        title: 'Günlük İngilizce Kelime Öğrenme',
        description: 'Her gün 20 yeni kelime öğrenmek ve tekrar etmek',
        targetSessions: 60,
        completedSessions: 45,
        targetMinutes: 300,
        completedMinutes: 225,
        technique: 'microlearning',
        deadline: '2024-06-01',
        priority: 'medium',
        completed: false,
        createdAt: '2024-01-01',
        category: 'habit',
        subject: 'İngilizce'
      },
      {
        id: '3',
        title: 'Fen Bilimleri Proje Ödevi',
        description: 'Güneş sistemi modeli hazırlamak ve sunum yapmak',
        targetSessions: 15,
        completedSessions: 15,
        targetMinutes: 450,
        completedMinutes: 480,
        technique: 'timeboxing',
        deadline: '2024-02-20',
        priority: 'high',
        completed: true,
        createdAt: '2024-01-20',
        category: 'project',
        subject: 'Fen Bilimleri'
      }
    ];
    setGoals(demoGoals);
  }, []);

  const addGoal = () => {
    if (!newGoal.title || !newGoal.deadline) return;
    
    const goal: Goal = {
      id: Date.now().toString(),
      title: newGoal.title!,
      description: newGoal.description || '',
      targetSessions: newGoal.targetSessions || 1,
      completedSessions: 0,
      targetMinutes: newGoal.targetMinutes || 60,
      completedMinutes: 0,
      technique: newGoal.technique || 'pomodoro',
      deadline: newGoal.deadline!,
      priority: newGoal.priority || 'medium',
      completed: false,
      createdAt: new Date().toISOString(),
      category: newGoal.category || 'study',
      subject: newGoal.subject || ''
    };
    
    setGoals(prev => [...prev, goal]);
    resetNewGoal();
    setShowAddGoal(false);
  };

  const resetNewGoal = () => {
    setNewGoal({
      title: '',
      description: '',
      targetSessions: 1,
      targetMinutes: 60,
      technique: 'pomodoro',
      deadline: '',
      priority: 'medium',
      category: 'study',
      subject: ''
    });
  };

  const deleteGoal = (goalId: string) => {
    setGoals(prev => prev.filter(goal => goal.id !== goalId));
  };

  const toggleGoalComplete = (goalId: string) => {
    setGoals(prev => prev.map(goal => 
      goal.id === goalId ? { ...goal, completed: !goal.completed } : goal
    ));
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-300 dark:border-yellow-800';
      case 'low': return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800';
      default: return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600';
    }
  };

  const getCategoryInfo = (categoryId: string) => {
    return GOAL_CATEGORIES.find(cat => cat.id === categoryId) || GOAL_CATEGORIES[0];
  };

  const getGoalProgress = (goal: Goal) => {
    const sessionProgress = (goal.completedSessions / goal.targetSessions) * 100;
    const minuteProgress = (goal.completedMinutes / goal.targetMinutes) * 100;
    return Math.min(sessionProgress, minuteProgress);
  };

  const getDaysUntilDeadline = (deadline: string) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const filteredGoals = goals.filter(goal => {
    const matchesFilter = filter === 'all' || 
      (filter === 'active' && !goal.completed) || 
      (filter === 'completed' && goal.completed);
    
    const matchesSearch = goal.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      goal.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || goal.category === selectedCategory;
    
    return matchesFilter && matchesSearch && matchesCategory;
  });

  const stats = {
    total: goals.length,
    completed: goals.filter(g => g.completed).length,
    active: goals.filter(g => !g.completed).length,
    overdue: goals.filter(g => !g.completed && getDaysUntilDeadline(g.deadline) < 0).length
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-3 sm:p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2 flex items-center">
                <Target className="mr-2 sm:mr-3 text-indigo-600" size={28} />
                Hedeflerim
              </h1>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
                Çalışma hedeflerinizi belirleyin ve ilerlemenizi takip edin
              </p>
            </div>
            <button
              onClick={() => setShowAddGoal(true)}
              className="bg-indigo-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold flex items-center justify-center space-x-2 hover:bg-indigo-700 transition-colors shadow-lg text-sm sm:text-base w-full sm:w-auto"
            >
              <Plus size={18} />
              <span>Yeni Hedef</span>
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Toplam</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
                </div>
                <Target className="text-indigo-600" size={20} />
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Tamamlanan</p>
                  <p className="text-xl sm:text-2xl font-bold text-green-600">{stats.completed}</p>
                </div>
                <CheckCircle2 className="text-green-600" size={20} />
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Aktif</p>
                  <p className="text-xl sm:text-2xl font-bold text-blue-600">{stats.active}</p>
                </div>
                <TrendingUp className="text-blue-600" size={20} />
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Geciken</p>
                  <p className="text-xl sm:text-2xl font-bold text-red-600">{stats.overdue}</p>
                </div>
                <AlertCircle className="text-red-600" size={20} />
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-3 lg:space-x-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Hedef ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Status Filter */}
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as any)}
                className="px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white min-w-0 sm:min-w-[120px]"
              >
                <option value="all">Tüm Hedefler</option>
                <option value="active">Aktif</option>
                <option value="completed">Tamamlanan</option>
              </select>

              {/* Category Filter */}
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white min-w-0 sm:min-w-[140px]"
              >
                <option value="all">Tüm Kategoriler</option>
                {GOAL_CATEGORIES.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.icon} {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Goals Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
          {filteredGoals.length === 0 ? (
            <div className="col-span-full text-center py-8 sm:py-12">
              <Target className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-lg sm:text-xl font-semibold text-gray-600 dark:text-gray-400 mb-2">
                Hedef bulunamadı
              </h3>
              <p className="text-sm sm:text-base text-gray-500 dark:text-gray-500 px-4">
                {searchTerm || selectedCategory !== 'all' || filter !== 'all' 
                  ? 'Arama kriterlerinize uygun hedef bulunamadı.'
                  : 'Henüz hedef eklenmemiş. İlk hedefinizi ekleyerek başlayın!'
                }
              </p>
            </div>
          ) : (
            filteredGoals.map((goal) => {
              const categoryInfo = getCategoryInfo(goal.category);
              const progress = getGoalProgress(goal);
              const daysLeft = getDaysUntilDeadline(goal.deadline);
              
              return (
                <div key={goal.id} className={`bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow ${goal.completed ? 'opacity-75' : ''}`}>
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3 sm:mb-4">
                    <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 ${categoryInfo.color} rounded-lg flex items-center justify-center text-white text-sm sm:text-lg flex-shrink-0`}>
                        {categoryInfo.icon}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className={`font-semibold text-sm sm:text-lg truncate ${goal.completed ? 'line-through text-gray-500' : 'text-gray-900 dark:text-white'}`}>
                          {goal.title}
                        </h3>
                        <div className="flex items-center space-x-1 sm:space-x-2 mt-1">
                          <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs border ${getPriorityColor(goal.priority)}`}>
                            {goal.priority === 'high' ? 'Yüksek' : goal.priority === 'medium' ? 'Orta' : 'Düşük'}
                          </span>
                          {goal.subject && (
                            <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-xs truncate max-w-20 sm:max-w-none">
                              {goal.subject}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-1 sm:space-x-2 flex-shrink-0">
                      {goal.completed && (
                        <Check className="text-green-500" size={16} />
                      )}
                      <button
                        onClick={() => toggleGoalComplete(goal.id)}
                        className={`p-1.5 sm:p-2 rounded-lg transition-colors ${
                          goal.completed 
                            ? 'bg-gray-500 text-white hover:bg-gray-600' 
                            : 'bg-green-500 text-white hover:bg-green-600'
                        }`}
                      >
                        <Check size={14} />
                      </button>
                      <button
                        onClick={() => deleteGoal(goal.id)}
                        className="p-1.5 sm:p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  {/* Description */}
                  {goal.description && (
                    <p className="text-gray-600 dark:text-gray-300 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-2">
                      {goal.description}
                    </p>
                  )}

                  {/* Progress */}
                  <div className="mb-3 sm:mb-4">
                    <div className="flex justify-between text-xs sm:text-sm text-gray-600 dark:text-gray-300 mb-1 sm:mb-2">
                      <span>İlerleme</span>
                      <span>{Math.round(progress)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 sm:h-2">
                      <div 
                        className={`${categoryInfo.color} h-1.5 sm:h-2 rounded-full transition-all duration-300`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 mb-3 sm:mb-4">
                    <div className="text-center">
                      <div className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-white">
                        {goal.completedSessions}/{goal.targetSessions}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Oturum</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-white">
                        {Math.floor(goal.completedMinutes)}/{goal.targetMinutes}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Dakika</div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                    <span className="flex items-center truncate">
                      <Calendar size={12} className="mr-1 flex-shrink-0" />
                      <span className="truncate">{new Date(goal.deadline).toLocaleDateString('tr-TR')}</span>
                    </span>
                    <span className={`flex items-center ml-2 flex-shrink-0 ${
                      daysLeft < 0 ? 'text-red-500' : daysLeft <= 7 ? 'text-yellow-500' : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      <Clock size={12} className="mr-1" />
                      <span className="whitespace-nowrap">
                        {daysLeft < 0 ? `${Math.abs(daysLeft)} gün gecikti` : 
                         daysLeft === 0 ? 'Bugün bitiyor' : 
                         `${daysLeft} gün kaldı`}
                      </span>
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Add Goal Modal */}
        {showAddGoal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 dark:text-white mb-4 sm:mb-6 flex items-center">
                <Target className="mr-2 text-indigo-600" size={20} />
                Yeni Hedef Ekle
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                    Hedef Başlığı *
                  </label>
                  <input
                    type="text"
                    value={newGoal.title || ''}
                    onChange={(e) => setNewGoal(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Örn: Matematik sınavına hazırlanmak"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                    Açıklama
                  </label>
                  <textarea
                    value={newGoal.description || ''}
                    onChange={(e) => setNewGoal(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Hedef hakkında detaylar..."
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                    Kategori
                  </label>
                  <select
                    value={newGoal.category || 'study'}
                    onChange={(e) => setNewGoal(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    {GOAL_CATEGORIES.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.icon} {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                    Ders
                  </label>
                  <select
                    value={newGoal.subject || ''}
                    onChange={(e) => setNewGoal(prev => ({ ...prev, subject: e.target.value }))}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Ders seçin (opsiyonel)</option>
                    {SUBJECTS.map(subject => (
                      <option key={subject} value={subject}>
                        {subject}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                    Hedef Oturum
                  </label>
                  <input
                    type="number"
                    value={newGoal.targetSessions || 1}
                    onChange={(e) => setNewGoal(prev => ({ ...prev, targetSessions: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    min="1"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                    Hedef Dakika
                  </label>
                  <input
                    type="number"
                    value={newGoal.targetMinutes || 60}
                    onChange={(e) => setNewGoal(prev => ({ ...prev, targetMinutes: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    min="1"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                    Teknik
                  </label>
                  <select
                    value={newGoal.technique || 'pomodoro'}
                    onChange={(e) => setNewGoal(prev => ({ ...prev, technique: e.target.value }))}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    {TIME_TECHNIQUES.map(technique => (
                      <option key={technique.id} value={technique.id}>
                        {technique.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                    Son Tarih *
                  </label>
                  <input
                    type="date"
                    value={newGoal.deadline || ''}
                    onChange={(e) => setNewGoal(prev => ({ ...prev, deadline: e.target.value }))}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                    Öncelik
                  </label>
                  <select
                    value={newGoal.priority || 'medium'}
                    onChange={(e) => setNewGoal(prev => ({ ...prev, priority: e.target.value as any }))}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="low">Düşük</option>
                    <option value="medium">Orta</option>
                    <option value="high">Yüksek</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 mt-4 sm:mt-6">
                <button
                  onClick={() => {
                    setShowAddGoal(false);
                    resetNewGoal();
                  }}
                  className="flex-1 bg-gray-500 text-white py-2 px-4 text-sm sm:text-base rounded-lg hover:bg-gray-600 transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={addGoal}
                  disabled={!newGoal.title || !newGoal.deadline}
                  className="flex-1 bg-indigo-500 text-white py-2 px-4 text-sm sm:text-base rounded-lg hover:bg-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Hedef Ekle
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Goals;